import { useState, useEffect, useMemo, createContext, useContext } from 'react';
import * as d3Zoom from 'd3-zoom';

import { FieldViewport } from '../models/index.js';
import type { DefaultZoomTransformGenerator } from '../utils/index.js';
import {
    getDefaultEditorZoomTransform,
    getDefaultZoomTransform,
    getFieldViewportForZoomFactor,
    getZoomLevels,
} from '../utils/index.js';

import { usePlayEditor } from './playEditor.js';

export interface FieldViewportValue {
    fieldViewport: FieldViewport;
    zoomTransform: d3Zoom.ZoomTransform;
    setZoomTransform: (zoomTransform: d3Zoom.ZoomTransform) => void;
    zoomIn: () => void;
    zoomOut: () => void;
    enableZoomIn: boolean;
    enableZoomOut: boolean;
    zoomLevels: number[];
}

export const FieldViewportContext = createContext<FieldViewportValue>(null);

export const useFieldViewport = (): FieldViewportValue => {
    return useContext(FieldViewportContext);
};

interface Props {
    getInitialZoomTransform?: DefaultZoomTransformGenerator;
}

export const FieldViewportProvider: FC<Props> = ({
    children,
    getInitialZoomTransform = getDefaultEditorZoomTransform,
}) => {
    const { dimensions, play, playData, PlayConfig } = usePlayEditor();
    const zoomLevels = getZoomLevels(PlayConfig, play?.field, play?.sport, dimensions);
    const defaultZoomTransform = useMemo(
        () =>
            dimensions && play
                ? getInitialZoomTransform({
                      PlayConfig,
                      play,
                      playData,
                      dimensions,
                      zoomLevels,
                  })
                : null,
        [PlayConfig, play, playData, dimensions, zoomLevels, getInitialZoomTransform]
    );
    const [zoomTransform, setZoomTransform] = useState<d3Zoom.ZoomTransform>(
        defaultZoomTransform ?? d3Zoom.zoomIdentity
    );

    const fieldViewport = useMemo(
        () => (dimensions && play ? getFieldViewportForZoomFactor(play, dimensions, zoomTransform.k) : null),
        [play, dimensions, zoomTransform]
    );

    const updateZoomFactor = (zoomFactor: number) => {
        if (fieldViewport) {
            setZoomTransform(
                getDefaultZoomTransform(
                    PlayConfig,
                    fieldViewport,
                    play.field,
                    play.players,
                    zoomFactor,
                    zoomLevels[zoomLevels.length - 1]
                )
            );
        }
    };

    useEffect(() => {
        if (fieldViewport && defaultZoomTransform) {
            if (zoomTransform.toString() === d3Zoom.zoomIdentity.toString()) {
                setZoomTransform(defaultZoomTransform);
            } else {
                updateZoomFactor(fieldViewport.zoomFactor);
            }
        }
        // Recompute the viewport only when the play, field dimensions, or flip options change.
        // Including zoomTransform/updateZoomFactor here creates a feedback loop while preserving an existing zoom.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [playData?.id, dimensions, play?.viewOptions?.flipField, play?.viewOptions?.flipFieldHorizontal]);

    const zoomIn = () => {
        const newZoomLevelIdx = zoomLevels.findIndex((level) => level > fieldViewport?.zoomFactor);
        updateZoomFactor(zoomLevels[newZoomLevelIdx]);
    };

    const zoomOut = () => {
        const reversedZoomLevels = zoomLevels.slice().reverse();
        const newZoomLevelIdx = reversedZoomLevels.findIndex((level) => level < fieldViewport?.zoomFactor);
        updateZoomFactor(reversedZoomLevels[newZoomLevelIdx]);
    };

    return (
        <FieldViewportContext.Provider
            value={{
                fieldViewport,
                zoomTransform,
                setZoomTransform,
                zoomIn,
                zoomOut,
                enableZoomIn: fieldViewport?.zoomFactor < zoomLevels[zoomLevels.length - 1],
                enableZoomOut: fieldViewport?.zoomFactor > 1,
                zoomLevels,
            }}
        >
            {children}
        </FieldViewportContext.Provider>
    );
};
